<template>
    <div class="newsletter-card" :data-newsletter-id="newsletter.id">
        <div class="newsletter-card__image">
            <img :src="newsletter.image" :alt="newsletter.title" />
        </div>
        <a :href="newsletter.preview_url"
            target="_blank"
            rel="noopener noreferrer"
            class="newsletter-card__icon newsletter-card__icon--premium"
            :aria-label="`Learn more about ${newsletter.title}`"
            :title="`Learn more about ${newsletter.title}`"
            v-if="newsletter.is_premium"
        >
            <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                <path d="M8.75 14.583V10.208C8.75 6.069 12.069 2.75 16.208 2.75H18.792C22.931 2.75 26.25 6.069 26.25 10.208V14.583H28.292C29.361 14.583 30.208 15.431 30.208 16.5V29.167C30.208 30.236 29.361 31.083 28.292 31.083H6.708C5.639 31.083 4.792 30.236 4.792 29.167V16.5C4.792 15.431 5.639 14.583 6.708 14.583H8.75ZM11.667 14.583H23.333V10.208C23.333 7.681 21.319 5.667 18.792 5.667H16.208C13.681 5.667 11.667 7.681 11.667 10.208V14.583Z" fill="white"/>
            </svg>
        </a>
        <button
            type="button"
            class="newsletter-card__icon" 
            @click="toggleSubscription"
            :class="{ 'newsletter-card__icon--selected': isSelected }"
            :aria-label="isSelected ? `Unsubscribe from ${newsletter.title}` : `Subscribe to ${newsletter.title}`"
            v-else
        >
            <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                <path v-if="isSelected" d="M4.5 2.875H30.5C31.3975 2.875 32.125 3.60254 32.125 4.5V30.5C32.125 31.3975 31.3975 32.125 30.5 32.125H4.5C3.60254 32.125 2.875 31.3975 2.875 30.5V4.5C2.875 3.60254 3.60254 2.875 4.5 2.875ZM15.8792 24L27.3698 12.5095L25.0717 10.2114L15.8792 19.4039L11.2831 14.8075L8.98498 17.1058L15.8792 24Z" fill="white"/>
                <path v-else d="M18.9586 16.0415L32.0837 16.0412V18.9579L18.9586 18.9582V32.083H16.0419V18.9582L2.91681 18.9585L2.91675 16.0418L16.0419 16.0415L16.0417 2.91701L18.9584 2.91699L18.9586 16.0415Z" fill="white"/>
            </svg>
        </button>
        <div class="newsletter-card__content">
            <div class="newsletter-card__frequency">{{ newsletter.frequency || 'Weekly' }}</div>
            <h3 class="newsletter-card__title">{{ newsletter.title }}</h3>
            <p class="newsletter-card__description">{{ newsletter.description }}</p>
            <a v-if="newsletter.preview_url" :href="newsletter.preview_url" class="newsletter-card__preview" target="_blank" rel="noopener noreferrer">
                <span v-if="newsletter.is_premium">Learn More</span>
                <span v-else>Preview</span>
                <svg class="newsletter-card__preview-icon" width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.66865 5.09936L4.00675 9.76127L3.24072 8.99524L7.90263 4.33333H3.79367V3.25H9.75198V9.20833H8.66865V5.09936Z" fill="#EF8125"/>
                </svg>
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NewsletterCard',
    props: {
        newsletter: {
            type: Object,
            required: true,
            validator: (newsletter) => {
                return newsletter && typeof newsletter.title === 'string';
            },
        },
        initiallySelected: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            isSelected: this.initiallySelected,
        };
    },
    mounted() {
        this.$emit('subscription-changed', {
            newsletter: this.newsletter,
            selected: this.isSelected,
            isInitialState: true,
        });
    },
    methods: {
        toggleSubscription() {
            this.isSelected = !this.isSelected;
            this.$emit('subscription-changed', {
                newsletter: this.newsletter,
                selected: this.isSelected,
                isInitialState: false,
            });
        }
    }
};
</script>

<style scoped>
.newsletter-card {
    position: relative;
    background: white;
    width: 245px;
    height: 274px;
    display: inline-block;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.newsletter-card__image {
    position: relative;
    width: 175px;
    height: 70px;
    overflow: hidden;
}

.newsletter-card__image img {
    width: 204px;
    height: 146px;
    object-fit: cover;
    margin-top: -28px;
}

.newsletter-card__icon {
    position: absolute;
    top: 0;
    right: 0;
    width: 70px;
    height: 70px;
    background-color: #45adbf;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    mix-blend-mode: multiply;
    transition: background-color 0.3s ease, transform 0.2s ease;
    text-decoration: none;
}

.newsletter-card__icon--premium {
    background-color: #45adbf;
    cursor: pointer;
}

.newsletter-card__icon--premium:hover {
    background-color: #3896a7;
    transform: scale(1.05);
}

button.newsletter-card__icon {
    cursor: pointer;
    background-color: #ef8125;
}

button.newsletter-card__icon:hover {
    background-color: #d16d1e;
    transform: scale(1.05);
}

button.newsletter-card__icon:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(239, 129, 37, 0.5);
}

button.newsletter-card__icon--selected {
    background-color: #28a745;
}

button.newsletter-card__icon--selected:hover {
    background-color: #218838;
}

.newsletter-card__content {
    padding: 13px;
    position: relative;
    height: calc(300px - 70px - 26px); /* Card height minus image height minus padding */
    display: flex;
    flex-direction: column;
}

.newsletter-card__frequency {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 3px;
}

.newsletter-card__title {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    font-size: 18px;
    color: #ef8125;
    margin: 0 0 10px;
    line-height: normal;
}

.newsletter-card__description {
    font-family: 'Montserrat', sans-serif;
    font-weight: normal;
    font-size: 14px;
    color: #363636;
    line-height: 18px;
    margin-bottom: 0;
    width: 229px;
    flex: 1;
}

.newsletter-card__preview {
    position: absolute;
    bottom: 13px;
    left: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;
}

.newsletter-card__preview:hover {
    color: #ef8125;
}

.newsletter-card__preview:hover svg {
    transform: translateX(2px);
}

.newsletter-card__preview-icon {
    transition: transform 0.2s ease;
    margin-left: -6px;
}
</style>